import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { PAYPAL_PRESENTMENT_CURRENCIES } from '@reverbdotcom/commons/src/currency';
import {
  core_apimessages_CheckoutPaymentMethod_Type as CheckoutPaymentMethodType,
} from '../../src/schemas/rql';
import { isCartEligible } from './checkout/ExpressPayPalButton';

export const MIN_PAY_LATER_AMOUNT_CENTS = 50_00;
export const MAX_PAY_LATER_CALLOUT_AMOUNT_CENTS = 350_00;
export const MAX_PAY_LATER_BUTTON_AMOUNT_CENTS = 2500_00;

export function useExpressPayPalAvailability({ currency, paymentMethodType, paypalCheckoutEligibilityAttributes }: {
  currency: string,
  paymentMethodType: CheckoutPaymentMethodType,
  paypalCheckoutEligibilityAttributes: {
    currencyCount: number,
    bundleCount: number,
    shopCount: number,
    isLocalPickupOnly: boolean,
    includesStandalonePaypal: boolean,
  },
}) {
  const { isPayPalCurrencySupported } = usePayPalCurrency({ fallbackCurrency: currency });

  const isExpressPayPalAvailable = (isPayPalCurrencySupported &&
    paymentMethodType !== CheckoutPaymentMethodType.PAYPAL &&
    !paypalCheckoutEligibilityAttributes.isLocalPickupOnly) || isCartEligible(paypalCheckoutEligibilityAttributes);

  return isExpressPayPalAvailable;
}

export function usePayPalCurrency({ fallbackCurrency }: { fallbackCurrency: string }): {
  isPayPalCurrencySupported: boolean,
  payPalCurrency: string
} {
  const user = useUser();
  const isPayPalCurrencySupported = PAYPAL_PRESENTMENT_CURRENCIES.includes(user.currency);
  const payPalCurrency = isPayPalCurrencySupported ? user.currency : fallbackCurrency;

  return {
    isPayPalCurrencySupported,
    payPalCurrency,
  };
}

export function usePayPalPayLaterButtonAvailability({ currency, amountCents, shopCountryCode }: {
  currency: string,
  amountCents: number,
  shopCountryCode: string;
}) {
  return useShowPayPalPayLater({
    minPrice: MIN_PAY_LATER_AMOUNT_CENTS,
    maxPrice: MAX_PAY_LATER_BUTTON_AMOUNT_CENTS,
    currency,
    amountCents,
    shopCountryCode,
  });
}

function useShowPayPalPayLater({ minPrice, maxPrice, currency, amountCents, shopCountryCode }: {
  minPrice: number;
  maxPrice: number;
  currency: string;
  amountCents: number;
  shopCountryCode: string;
}) {
  const user = useUser();

  const inPriceRange = amountCents > minPrice && amountCents < maxPrice;
  const isUSDTransaction = user.currency === 'USD' && currency === 'USD';
  const isUSShop = shopCountryCode === 'US';

  return inPriceRange && isUSDTransaction && isUSShop;
}
